/**
 * NotifyUsersModal - "you're about to email these people" gate.
 *
 * Atomic Design role: Organism. Composes the ConfirmModal shell with the
 * shared RecipientList so the admin prompt answers the same question, in the
 * same shape, as the technician's and the reviewer's verification prompts:
 * WHO, at WHICH address.
 *
 * Saving a work order or an inspection quietly fans out email — to the new
 * assignee, to whoever watches that customer, to the approver pool, to the
 * customer contact. Admins and super admins get this prompt first: every
 * recipient named, with the reason they're on the list, and a checkbox each.
 * A minor typo fix on a Friday afternoon no longer has to wake anybody up.
 *
 * Three answers, matching the pattern the verification prompts established —
 * Cancel means "do nothing at all", and there are two ways to say yes:
 *
 *	 Cancel				 nothing is saved
 *	 Don't Email Anyone	 save, email nobody
 *	 Email & Save		 save AND email whoever is still checked
 *
 * Selection lives here rather than in the caller: every call site wants the
 * same default (everyone checked) and the same reset-on-open behaviour, and
 * a gate that made each one re-implement that would drift.
 */
import React, { useEffect, useMemo, useState } from 'react';
import ConfirmModal from '../ConfirmModal/ConfirmModal';
import { RecipientList } from '../../molecules/RecipientList';
import type { NotifyPreviewEvent, NotifyPreviewRecipient } from '../../../services/notificationService';

/* ============================================
 * TYPE DEFINITIONS
 * ============================================ */

export interface NotifyUsersModalProps {
	/** Controls visibility. Selection resets to "everyone" each time it opens. */
	isOpen: boolean;
	/** Everyone the pending save would email. */
	recipients: NotifyPreviewRecipient[];
	/** What's about to happen — "Assignment", "Completion". Shown as context. */
	events?: NotifyPreviewEvent[];
	/** Save and email the still-checked recipients. */
	onConfirm: (emails: string[]) => void;
	/** Save without emailing anyone. */
	onSkip: () => void;
	/** Dismiss without saving. */
	onCancel: () => void;
	/** Header text. Defaults to "Notify users". */
	title?: string;
	/** Sentence prefix over the list, e.g. "Saving this work order will email". */
	lead?: string;
	/** Disables every button while the save is in flight. */
	loading?: boolean;
}

/* ============================================
 * COMPONENT
 * ============================================ */

export const NotifyUsersModal: React.FC<NotifyUsersModalProps> = ({
	isOpen,
	recipients,
	events,
	onConfirm,
	onSkip,
	onCancel,
	title = 'Notify users',
	lead = 'Saving will email',
	loading = false,
}) => {
	/* Checked addresses. Everyone starts checked — the prompt exists to let an
	   admin make an exception, not to make them re-approve the normal case. */
	const [selected, setSelected] = useState<string[]>([]);

	/* Re-arm on every open, and whenever the resolved list changes underneath
	   (a different assignee, a re-opened form) — stale checkboxes here would
	   mean silently emailing the wrong set. */
	useEffect(() => {
		if (isOpen) {
			setSelected(recipients.map((recipient) => recipient.email));
		}
	}, [isOpen, recipients]);

	const allChecked = selected.length === recipients.length && recipients.length > 0;
	const noneChecked = selected.length === 0;

	/* "About: Assignment, Completion" — one line of context so the admin knows
	   which part of the save is doing the emailing. */
	const eventSummary = useMemo(
		() => (events ?? []).map((event) => event.label).filter(Boolean).join(', '),
		[events],
	);

	const handleToggle = (email: string, isChecked: boolean) => {
		setSelected((prev) => {
			const without = prev.filter((existing) => existing.toLowerCase() !== email.toLowerCase());
			return isChecked ? [...without, email] : without;
		});
	};

	const handleToggleAll = () => {
		setSelected(allChecked ? [] : recipients.map((recipient) => recipient.email));
	};

	/* Unchecking everyone and pressing the primary button means the same thing
	   as "Don't Email Anyone" — so it does the same thing, rather than dead-
	   ending on a disabled button. The count rides along once there's more
	   than one, so "Email & Save (3)" says how many are still checked. */
	const confirmLabel = noneChecked
		? 'Save Without Emailing'
		: `Email & Save${selected.length > 1 ? ` (${selected.length})` : ''}`;

	return (
		<ConfirmModal isOpen={isOpen} onClose={onCancel} onConfirm={noneChecked ? onSkip : () => onConfirm(selected)} title={title} confirmLabel={confirmLabel} cancelLabel="Cancel" secondaryLabel={noneChecked ? undefined : "Don't Email Anyone"} onSecondary={noneChecked ? undefined : onSkip} loading={loading} className="notify-users-modal" dataRef="organism-notify-users-modal">
			{eventSummary && (
				<p className="notify-users-modal__events mb-12px text-13px font-sans font-medium text-brand-text-muted uppercase tracking-wide">
					{eventSummary}
				</p>
			)}

			{/* Same block the verification prompts render — one answer to "who
			    exactly gets this?", here with a checkbox per row. */}
			<RecipientList recipients={recipients} lead={lead} selectable selectedEmails={selected} onToggleRecipient={handleToggle} emptyText="Nobody would be emailed by this change." />

			{recipients.length > 1 && (
				<button type="button" onClick={handleToggleAll} disabled={loading} className="notify-users-modal__toggle-all mt-8px min-h-44px text-13px font-sans font-medium text-brand-primary hover:underline disabled:opacity-50 disabled:cursor-not-allowed">
					{allChecked ? 'Uncheck everyone' : 'Check everyone'}
				</button>
			)}

			<p className="notify-users-modal__note mt-12px text-13px font-sans text-brand-text-muted">
				Uncheck anyone who shouldn't hear about this change. The record is saved and logged either way — only the email is skipped.
			</p>
		</ConfirmModal>
	);
};

export default NotifyUsersModal;
